/**
 * Logo / brand marquee — an infinite horizontal "works with" band of
 * the runtimes Axon wires into and the venues it executes on.
 *
 * Two tracks moving in opposite directions (runtimes ←, venues →).
 * Hover pauses; reduced-motion users get a static, wrapped wall.
 *
 * Marks are the official brand SVGs in packages/landing/public/logos/
 * (sourced from each company's own brand kit). They ship in their
 * native colors — mostly near-black, authored for light backgrounds —
 * so we normalize them to a uniform white silhouette via CSS filter:
 * a cohesive monochrome logo wall that's legible on the dark surface
 * and doesn't fight the green accent. Drop a differently-colored file
 * in and it still works; the filter flattens it.
 *
 * Only brands with an official, downloadable brand kit are listed
 * here. Continue + Hermes have no official logo source, so they're
 * omitted from the wall (still present as text runtimes elsewhere).
 */

interface Brand {
  /** Matches the filename in /public/logos/<id>.svg */
  id: string;
  label: string;
}

const RUNTIMES: Brand[] = [
  { id: "claude", label: "Claude" },
  { id: "codex", label: "Codex" },
  { id: "cursor", label: "Cursor" },
  { id: "windsurf", label: "Windsurf" },
  { id: "cline", label: "Cline" },
];

const VENUES: Brand[] = [
  { id: "hyperliquid", label: "Hyperliquid" },
  { id: "uniswap", label: "Uniswap V3" },
  { id: "pancakeswap", label: "PancakeSwap V3" },
  { id: "aave", label: "Aave V3" },
  { id: "gmx", label: "GMX v2" },
  { id: "phoenix", label: "Phoenix" },
  { id: "lighter", label: "Lighter" },
];

// One continuous row: runtimes then venues, rotating in a single
// direction (no second counter-scrolling row).
const BRANDS: Brand[] = [...RUNTIMES, ...VENUES];

function Brand({ b }: { b: Brand }) {
  return (
    <div className="lm-brand" title={b.label}>
      <img
        className="lm-logo"
        src={`/logos/${b.id}.svg`}
        alt={b.label}
        loading="lazy"
        decoding="async"
        onError={(e) => {
          // Missing/broken file → fall back to a clean wordmark.
          const img = e.currentTarget;
          img.style.display = "none";
          const fb = img.nextElementSibling as HTMLElement | null;
          if (fb) fb.style.display = "inline";
        }}
      />
      <span className="lm-fallback">{b.label}</span>
    </div>
  );
}

function Track({ items }: { items: Brand[] }) {
  // Doubled so the -50% keyframe loops seamlessly.
  const doubled = [...items, ...items];
  return (
    <div className="lm-viewport">
      <div className="lm-track">
        {doubled.map((b, i) => (
          <Brand key={`${b.id}-${i}`} b={b} />
        ))}
      </div>
    </div>
  );
}

export function LogoMarquee() {
  return (
    <section className="lm" aria-label="Runtimes and venues Axon works with">
      <div className="lm-eyebrow">Works in every agent · trades on every major venue</div>
      <Track items={BRANDS} />

      <style>{`
        .lm {
          position: relative;
          width: 100%;
          padding: 40px 0 28px;
          overflow: hidden;
          border-top: 1px solid var(--at-hairline);
          border-bottom: 1px solid var(--at-hairline);
          background:
            radial-gradient(ellipse at center, rgba(74,222,128,0.035) 0, transparent 60%);
        }
        .lm-eyebrow {
          font-family: var(--at-font-mono);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.34em;
          text-transform: uppercase;
          color: var(--at-muted);
          text-align: center;
          margin-bottom: 30px;
        }

        .lm-viewport {
          position: relative;
          overflow: hidden;
          padding: 14px 0;
          /* Feather both ends so logos emerge/dissolve into the page
             instead of being hard-clipped at a visible edge. */
          -webkit-mask-image: linear-gradient(to right,
            transparent 0, #000 8%, #000 92%, transparent 100%);
          mask-image: linear-gradient(to right,
            transparent 0, #000 8%, #000 92%, transparent 100%);
        }
        .lm-track {
          display: flex;
          align-items: center;
          width: max-content;
          gap: 28px;
          will-change: transform;
        }
        .lm-track { animation: lm-scroll 60s linear infinite; }

        @keyframes lm-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }

        /* Every logo gets an equal fixed cell so wordmark lockups
           (Cline) and bare icon marks (Cursor, GMX) read at the same
           optical weight — the standard logo-wall normalization. */
        .lm-brand {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 168px;
          height: 48px;
          opacity: 0.6;
          transition: opacity var(--at-dur-med) var(--at-ease);
        }
        .lm-brand:hover { opacity: 1; }
        .lm-logo {
          max-height: 30px;
          max-width: 132px;
          width: auto;
          height: auto;
          display: block;
          object-fit: contain;
          /* Flatten any source color to a clean white silhouette so
             the wall is uniform + legible on the dark surface. */
          filter: brightness(0) invert(1);
        }
        .lm-fallback {
          display: none;
          font-family: var(--at-font-mono);
          font-size: 22px;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--at-fg);
          white-space: nowrap;
        }

        @media (max-width: 700px) {
          .lm-brand { width: 124px; height: 40px; }
          .lm-logo { max-height: 24px; max-width: 100px; }
          .lm-track { gap: 16px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .lm-viewport {
            -webkit-mask-image: none;
            mask-image: none;
          }
          .lm-track {
            animation: none !important;
            width: 100%;
            flex-wrap: wrap;
            justify-content: center;
            gap: 28px 56px;
          }
          /* Hide the duplicated half so the static wall isn't doubled. */
          .lm-track > :nth-child(n + ${BRANDS.length + 1}) { display: none; }
        }
      `}</style>
    </section>
  );
}
